// Global PrimeReact/PrimeIcons CSS are imported in index.tsx to control load order.

import { Routes, Route, Navigate } from "react-router-dom";

import { ReduxState } from "./redux/redux-state";
import { useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import InvisiblyApiService from "./services/api/invisibly-api.service";
import ErrorLogService from "./services/error-log/error-log.service";
import { signOut } from "./redux/user/user.async-thunks";
import { getIsUserAuthenticated, getIsUserClient, getIsUserOrg, getIsUserSuperAdmin } from "./redux/user/user.selectors";
import AuthService from "./services/auth/auth.service";
import { setUser } from "./redux/user/user.slice";
import AsyncStorageService, { AsyncStorageKeys } from "./services/storage/async-storage.service";
import { User } from "./services/user/user.types";
import { v4 as uuid } from "uuid";
import { B2BMessage } from './components/message/b2bmessage';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import ScrollToTop from './components/scroll-to-top/scroll-to-top';
import FirebaseService from "./services/firebase/firebase.service";
import VersionService from "./services/version/version.service";
import VersionUpdateNotification from "./components/version-update-notification/version-update-notification";
import { gsap } from 'gsap';
import { useGSAP } from "@gsap/react";
import { ScrambleTextPlugin } from "gsap/ScrambleTextPlugin";
import { SplitText } from "gsap/SplitText";
import ROUTES, { RouterProps } from './routes';

gsap.registerPlugin(useGSAP, ScrambleTextPlugin, SplitText);
type AppProps = PropsFromRedux;
const App = ({ email, extUserId, isAuthenticated, isClientUser, isOrgUser, isSuperAdminUser, jwt, authSignOut }: AppProps): JSX.Element => {
  const [areServicesReady, setAreServicesReady] = useState(false);
  const [isUserReady, setIsUserReady] = useState(false);

  useEffect(() => {
    void initApp();
  }, []);

  useEffect(() => {
    InvisiblyApiService.setAuthorizationToken(jwt);
  }, [jwt]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    AuthService.initUserAuthInvalidInterceptor(authSignOut);
  }, [isAuthenticated]);

  useEffect(() => {
    if (areServicesReady && !isUserReady) {
      void (extUserId ? initForUsers() : initForNonUsers());
    }
  }, [extUserId, areServicesReady]);

  async function initApp(): Promise<void> {
    try {
      await FirebaseService.init();
      InvisiblyApiService.init();

      // Register service worker for version checking
      if (process.env.NODE_ENV === 'production') {
        await VersionService.registerServiceWorker();
      }
    } catch (error) {
      ErrorLogService.logError(error, 'App', 'initApp');
    }

    setAreServicesReady(true);
  }

  function initForUsers(): void {
    // void AuthService.linkDeviceIdToUser(extUserId);
    setIsUserReady(true);
  }

  async function initForNonUsers(): Promise<void> {
    try {
      let deviceId = await AsyncStorageService.localStorage.retrieveData<string>(AsyncStorageKeys.deviceId);

      // TODO: temp solution to store device as a user yet
      const tokenExists = false;

      if (deviceId) {
        // tokenExists = await AuthService.checkDeviceIdLinked(deviceId);
      } else {
        deviceId = uuid();
        await AsyncStorageService.localStorage.storeData(AsyncStorageKeys.deviceId, deviceId);
      }

      const newUser: User | null = null;

      if (tokenExists) {
        //TODO landingScreenOverride.current = 'LandingScreen';
      } else {
        // newUser = await AuthService.createNewAnonymousUser(deviceId);
      }

      if (newUser) {
        setUser({user: newUser, access: jwt, refresh: ''});
        setIsUserReady(true);
      } else {
        setIsUserReady(true);
      }
    } catch (error) {
      ErrorLogService.logError(error, 'root-nav', 'reinitiateUserSessionWithToken');
      // landingScreenOverride.current = 'DeviceIdErrorScreen';
      setIsUserReady(true);
    }
  }

  interface RouteWrapperProps {
    children: JSX.Element;
    redirectTo: string;
  }

  function IsClientUser({ children, redirectTo }: RouteWrapperProps) {
    return isClientUser || isOrgUser || isSuperAdminUser ? children : <Navigate to={redirectTo} />;
  }
  function IsOrgUser({ children, redirectTo }: RouteWrapperProps) {
    return isOrgUser || isSuperAdminUser ? children : <Navigate to={redirectTo} />;
  }
  function RequireAuth({ children, redirectTo }: RouteWrapperProps) {
    return isAuthenticated ? children : <Navigate to={redirectTo} />;
  }
  function RequireSuperAdmin({ children, redirectTo }: RouteWrapperProps) {
    return isSuperAdminUser ? children : (
      <Navigate
        replace
        to={redirectTo}
      />
    );
  }

  function renderElement(route: RouterProps) {
    if (route.guard === 'isAuthenticated') {
      return (
        <RequireAuth redirectTo={route.redirect ?? ''}>
          {route.element as JSX.Element}
        </RequireAuth>
      );
    }
    if (route.guard === 'isSuperAdmin') {
      return (
        <RequireSuperAdmin redirectTo={route.redirect ?? ''}>
          {route.element as JSX.Element}
        </RequireSuperAdmin>
      );
    }
    if (route.guard === 'isOrg') {
      return (
        <IsOrgUser redirectTo={route.redirect ?? ''}>
          {route.element as JSX.Element}
        </IsOrgUser>
      );
    }
    if (route.guard === 'isClient') {
      return (
        <IsClientUser redirectTo={route.redirect ?? ''}>
          {route.element as JSX.Element}
        </IsClientUser>
      );
    }

    return (
      route.element as JSX.Element
    );
  }

  return (
    <I18nextProvider i18n={i18n}>
      <div className="App">
        <B2BMessage />
        <ScrollToTop />
        <VersionUpdateNotification />

        <Routes>
          {ROUTES.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={renderElement(route)}
            />
          ))}

        </Routes>
      </div>
    </I18nextProvider>
  );
};


const mapStateToProps = (state: ReduxState) => ({
  email: state.userState.email,
  extUserId: state.userState.extUserId,
  isClientUser: getIsUserClient(state),
  isOrgUser: getIsUserOrg(state),
  isAuthenticated: getIsUserAuthenticated(state),
  isSuperAdminUser: getIsUserSuperAdmin(state),
  jwt: state.userState.access,
});

const mapDispatchToProps = {
  authSignOut: signOut,
  setUserState: setUser,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(App);
